import React from "react";
import { Link } from "react-router-dom";
import { Button, Input } from "../../components/ui";
import Alert from "../../components/ui/Alert/Alert";
import { useFormValidation } from "../../hooks";
import { commonValidationRules } from "../../utils/validation";
import { AuthService } from "../../services/authService";

interface ForgotForm {
  email: string;
}

const ForgotPasswordPage: React.FC = () => {
  const [sent, setSent] = React.useState(false);

  const form = useFormValidation<ForgotForm>({
    initialValues: { email: "" },
    validationRules: { email: commonValidationRules.email },
    onSubmit: async (values) => {
      // ponytail: fire-and-forget — never reveal whether account exists
      await AuthService.forgotPassword(values.email);
      setSent(true);
    },
  });

  return (
    <div className="bg-zinc-950 text-white min-h-screen flex flex-col">
      <header className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-8 py-4 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-sm">
        <a href="/" className="flex items-center gap-3 select-none">
          <img src="/logo192.png" alt="RoleWeave" className="w-8 h-8 object-contain" />
          <span className="text-xl font-light tracking-widest">
            Role<span className="text-amber-400 font-normal">Weave</span>
          </span>
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 pt-20">
        <div className="w-full max-w-md">
          <p className="text-amber-400/80 text-xs tracking-[0.45em] uppercase text-center mb-3">
            Account recovery
          </p>
          <h1 className="text-3xl md:text-4xl font-thin text-center text-white mb-10 tracking-wide">
            Forgot password?
          </h1>

          <div className="border border-zinc-800 bg-zinc-900 p-8">
            {sent ? (
              <Alert variant="success">
                If an account exists for that email, a reset link has been sent. Check your inbox.
              </Alert>
            ) : (
              <form onSubmit={form.handleSubmit}>
                <div className="mb-6">
                  <Input
                    type="email"
                    id="email"
                    label="Email"
                    value={form.values.email}
                    onChange={form.handleChange("email")}
                    onBlur={form.handleBlur("email")}
                    error={form.errors.email}
                    fullWidth
                    variant="filled"
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  disabled={form.isSubmitting}
                >
                  {form.isSubmitting ? "Sending..." : "Send reset link"}
                </Button>
              </form>
            )}
            <div className="mt-6 text-center text-sm text-zinc-400">
              Remember your password?{" "}
              <Link
                to="/login"
                className="text-amber-400 hover:text-amber-300 transition-colors duration-200"
              >
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="py-10 px-8 border-t border-zinc-900 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2 select-none">
          <img src="/logo192.png" alt="RoleWeave" className="w-5 h-5 object-contain opacity-70" />
          <span className="text-zinc-500 text-xs tracking-widest">
            Role<span className="text-amber-400/80">Weave</span>
          </span>
        </div>
        <p className="text-zinc-500 text-xs">© 2026 RoleWeave. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default ForgotPasswordPage;
